import { useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import ACL_RELATIONSHIPS from "acl-relationships";
import { formatError } from "helpers/formatError";
import useAuthorized from "hooks/useAuthorized";
import SwoogoLastSynced from "routes/swoogo/SwoogoLastSynced";
import { swoogoKeys } from "routes/swoogo/queryKeys";
import useSwoogoConnected from "routes/swoogo/useSwoogoConnected";
import SwoogoService from "services/Swoogo";

// "Sync Everything" also refetches the records stored before their custom
// field definitions, which the incremental "Sync Records" never asks for again.
const SYNC_OPTIONS = [
  {
    scope: "everything",
    label: "Sync Everything",
    description:
      "Updates the custom field definitions, then fetches every event in the visible folders in full.",
  },
  {
    scope: "records",
    label: "Sync Records",
    description:
      "Fetches the changes to live events since their last sync, using the stored field definitions.",
  },
];

const SyncOption = ({ label, description, onSelect }) => {
  const labelId = useId();
  const descriptionId = useId();

  return (
    <DropdownMenuItem
      className="tw:flex-col tw:items-start tw:gap-0.5"
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      onClick={onSelect}
    >
      <span id={labelId}>{label}</span>
      <span id={descriptionId} className="tw:text-xs tw:text-muted-foreground">
        {description}
      </span>
    </DropdownMenuItem>
  );
};

SyncOption.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export const SwoogoSyncButton = () => {
  const queryClient = useQueryClient();
  const canSync = useAuthorized([ACL_RELATIONSHIPS.swoogo.syncData]);
  const { canSync: hasStoredCredentials, syncStatus } = useSwoogoConnected();
  const [isTriggering, setIsTriggering] = useState(false);

  const isBusy = isTriggering || syncStatus === "pending";
  const isDisabled = !canSync || !hasStoredCredentials;

  const handleSync = async scope => {
    setIsTriggering(true);
    try {
      await SwoogoService.syncData(scope);
      toast.success("Sync started. Updated data will appear shortly.");
      queryClient.invalidateQueries({ queryKey: swoogoKeys.status() });
    } catch (error) {
      toast.error(formatError(error, "Could not start sync"));
    } finally {
      setIsTriggering(false);
    }
  };

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger render={<span className="tw:flex" />}>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                className="tw:size-5"
                disabled={isBusy || isDisabled}
                aria-label="Sync"
              />
            }
          >
            <RefreshCw className={cn(isBusy && "tw:animate-spin")} />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">
          {isDisabled ? "Syncing is disabled for this account" : "Sync"}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="start" className="tw:w-72">
        <DropdownMenuGroup>
          {SYNC_OPTIONS.map(({ scope, label, description }) => (
            <SyncOption
              key={scope}
              label={label}
              description={description}
              onSelect={() => handleSync(scope)}
            />
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const SwoogoSyncNote = () => {
  const { syncStatus, lastSyncedAt, syncError, syncSkipped, syncSkippedCount } =
    useSwoogoConnected();

  return (
    <SwoogoLastSynced
      syncStatus={syncStatus}
      lastSyncedAt={lastSyncedAt}
      syncError={syncError}
      syncSkipped={syncSkipped}
      syncSkippedCount={syncSkippedCount}
    />
  );
};
